import type { SceneNodeItem } from "../scene/SceneNodeItem";
import type { ItemStoreEvents } from "../itemStore.store";
import { createNewShapePath, resizeShapePath } from "./sceneShapeFunctions";
import type { SceneShapeData } from "./sceneShapeData";
import { shapeDefaults, shapeSizeDefaults } from "./sceneShapes.store";

export const sceneShapeStoreHooks: Partial<ItemStoreEvents<SceneNodeItem<"shape">>> = {
  async beforeCreate(item) {
    // Initialize data if not present
    if (!item.data) {
      item.data = {
        shapeType: shapeDefaults.shapeType,
        title: "",
        path: "",
        fill: shapeDefaults.fill,
        stroke: shapeDefaults.stroke,
        strokeWidth: shapeDefaults.strokeWidth,
        strokeStyle: shapeDefaults.strokeStyle,
        radius: shapeDefaults.radius
      };
    }

    const width = item.width || shapeSizeDefaults.width;
    const height = item.height || shapeSizeDefaults.height;

    // Apply shape data defaults
    item.data.fill = item.data.fill ?? shapeDefaults.fill;
    item.data.stroke = item.data.stroke ?? shapeDefaults.stroke;
    item.data.strokeWidth = item.data.strokeWidth ?? shapeDefaults.strokeWidth;
    item.data.strokeStyle = item.data.strokeStyle ?? shapeDefaults.strokeStyle;
    item.data.shapeType = item.data.shapeType ?? shapeDefaults.shapeType;
    item.data.radius = item.data.radius ?? shapeDefaults.radius;
    item.data.title = item.data.title ?? "";

    // Generate path if not provided
    if (!item.data.path) {
      const path = createNewShapePath(
        item.data.shapeType || shapeDefaults.shapeType,
        width,
        height,
        item.data.radius || shapeDefaults.radius
      );
      item.data.path = path;
    }

    return item;
  },

  beforeUpdate(updatedItem, updatedData, oldItem) {
    const changes: Partial<SceneNodeItem<"shape">> = {};
    
    // Handle shape type changes
    if (updatedData.data?.shapeType && updatedData.data.shapeType !== "polygon") {
      if (!updatedData.data.path) {
        if (!changes.data) changes.data = {} as SceneShapeData;
        (changes.data as SceneShapeData).path = createNewShapePath(
          updatedData.data.shapeType,
          updatedItem.width || shapeSizeDefaults.width,
          updatedItem.height || shapeSizeDefaults.height,
          updatedItem.data.radius || shapeDefaults.radius
        );
      }
    }

    // Handle size changes
    if (oldItem && updatedData.data?.path == null) {
      if (oldItem.width != updatedItem.width || oldItem.height != updatedItem.height) {
        if (updatedItem.data.shapeType == 'polygon') {
          if (!changes.data) changes.data = {} as SceneShapeData;
          (changes.data as SceneShapeData).path = resizeShapePath(
            updatedItem.data.path,
            oldItem.width || shapeSizeDefaults.width,
            oldItem.height || shapeSizeDefaults.height,
            updatedData.width || updatedItem.width || shapeSizeDefaults.width,
            updatedData.height || updatedItem.height || shapeSizeDefaults.height
          );
        } else {
          if (!changes.data) changes.data = {} as SceneShapeData;
          (changes.data as SceneShapeData).path = createNewShapePath(
            updatedItem.data.shapeType || shapeDefaults.shapeType,
            updatedItem.width || shapeSizeDefaults.width,
            updatedItem.height || shapeSizeDefaults.height,
            updatedItem.data.radius || shapeDefaults.radius
          );
        }
      }
    }

    return changes;
  },

  afterUpdate(_updatedItem, updatedData) {
    // Update defaults if shape properties were changed
    const data = updatedData.data;
    if (!data) {
      return;
    }
    
    if (data.shapeType && data.shapeType !== "polygon") {
      shapeDefaults.shapeType = data.shapeType;
    }
    if (data.fill != null) {
      shapeDefaults.fill = data.fill;
    }
    if (data.stroke != null) {
      shapeDefaults.stroke = data.stroke;
    }
    if (data.strokeWidth != null) {
      shapeDefaults.strokeWidth = data.strokeWidth;
    }
    if (data.strokeStyle != null) {
      shapeDefaults.strokeStyle = data.strokeStyle;
    }
  }
};
